import { DownOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Dropdown, Empty, Flex, Skeleton, Tag } from 'antd';

import { NewsletterCampaign, NewsletterCampaignStats } from '@hooks-api';
import { getApiErrorMessage, getDateDefault } from '@utils';

import * as S from './styled';
import {
  CAMPAIGN_HEADER_BLOCK,
  isAttributable,
  tagStatusChip,
  tagStatusConsequence,
  tagStatusInstruction,
  tagStatusReason,
} from './tagStatus';

interface Props {
  campaigns: NewsletterCampaign[];
  selectedId: number | null;
  onSelect: (campaignId: number) => void;
  isLoadingCampaigns: boolean;
  campaignsError: unknown;
  onRetryCampaigns: () => void;
  stats: NewsletterCampaignStats | null;
  isLoadingStats: boolean;
  statsError: unknown;
}

/** A tile value with an optional share-of-sent rate under the label. */
const Metric = ({ label, value, rate }: { label: string; value: number; rate?: number | null }) => (
  <S.Metric>
    <S.MetricValue>{value}</S.MetricValue>
    <S.MetricLabel>
      {label}
      {rate !== null && rate !== undefined && <S.MetricRate> · {rate}%</S.MetricRate>}
    </S.MetricLabel>
  </S.Metric>
);

/**
 * Per-campaign stats for the marketer (plan §7 PR 2 / §15). Campaigns are listed
 * read-only from Listmonk — composing and sending happen there — and the numbers
 * are the Listmonk send count plus Postmark engagement, which owns opens/clicks
 * on the VPN-only deployment (§5.1 Option B). Purely presentational; the page
 * owns the data via `useNewsletterStats`.
 */
const CampaignStats = ({
  campaigns,
  selectedId,
  onSelect,
  isLoadingCampaigns,
  campaignsError,
  onRetryCampaigns,
  stats,
  isLoadingStats,
  statsError,
}: Props) => {
  const selected = campaigns.find((campaign) => campaign.id === selectedId);
  const campaignLabel = (campaign: NewsletterCampaign) =>
    `${campaign.name} · ${getDateDefault(campaign.createdAt)}`;
  // The list call carries every campaign's tag status, so a campaign that will
  // report nothing can be spotted in the picker — before it is sent, not after.
  const campaignItem = (campaign: NewsletterCampaign) =>
    isAttributable(campaign.tagStatus) ? (
      campaignLabel(campaign)
    ) : (
      <Flex align="center" gap={8}>
        {campaignLabel(campaign)}
        <Tag color="warning">{tagStatusChip(campaign.tagStatus)}</Tag>
      </Flex>
    );

  // A Dropdown rather than a Select: antd's Select ships CSS that jsdom's
  // selector engine can't parse, which breaks every unit test that renders this
  // panel. Same interaction, testable.
  const selector = campaigns.length > 0 && (
    <Dropdown
      trigger={['click']}
      menu={{
        selectable: true,
        selectedKeys: selectedId === null ? [] : [String(selectedId)],
        style: { maxHeight: 320, overflowY: 'auto' },
        items: campaigns.map((campaign) => ({ key: String(campaign.id), label: campaignItem(campaign) })),
        onClick: ({ key }) => onSelect(Number(key)),
      }}
    >
      <Button loading={isLoadingCampaigns}>
        {selected ? campaignLabel(selected) : 'Pick a campaign'}
        <DownOutlined />
      </Button>
    </Dropdown>
  );

  const share = (value: number) =>
    stats && stats.sent > 0 ? Math.round((value / stats.sent) * 100) : null;

  const body = () => {
    if (campaignsError) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Couldn't load campaigns"
          description={getApiErrorMessage(
            campaignsError,
            'Listmonk did not answer. Campaign stats need LISTMONK_BASE_URL and API credentials configured for this environment.'
          )}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={onRetryCampaigns}>
              Try again
            </Button>
          }
        />
      );
    }
    if (isLoadingCampaigns) return <Skeleton active paragraph={{ rows: 2 }} />;
    if (campaigns.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No campaigns in Listmonk yet. Compose one there, then come back for its numbers."
        />
      );
    }
    if (statsError) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Couldn't load stats for this campaign"
          description={getApiErrorMessage(statsError, 'The stats request failed. Try another campaign.')}
        />
      );
    }
    if (isLoadingStats || !stats) return <Skeleton active paragraph={{ rows: 2 }} />;

    const { engagement, tagStatus } = stats;
    const attributable = isAttributable(tagStatus);
    // An unattributable campaign reports zeros for a tag no message carried, and
    // those zeros read as "nobody opened it" — so hide them. But headers are
    // mutable while sent mail is not: if numbers came back anyway, the send *was*
    // tagged at the time and the numbers are real, so they stay.
    const hasNumbers =
      !!engagement &&
      engagement.opened + engagement.clicked + engagement.bounced + engagement.spamComplaints > 0;
    const showEngagement = !!engagement && (attributable || hasNumbers);

    return (
      <Flex vertical gap={12}>
        {/* Only when Postmark could have answered. Without the broadcast token
            the panel already says so below, and telling someone to edit Listmonk
            headers would point them at a fix that changes nothing here. */}
        {!attributable && stats.postmarkAvailable && (
          <Alert
            type="warning"
            showIcon
            message="Postmark can't attribute this campaign"
            description={
              <Flex vertical gap={8}>
                <span>
                  {tagStatusReason(tagStatus)} {tagStatusInstruction(tagStatus)}{' '}
                  {tagStatusConsequence(tagStatus, hasNumbers)}
                </span>
                <S.HeaderBlock>{CAMPAIGN_HEADER_BLOCK}</S.HeaderBlock>
              </Flex>
            }
          />
        )}

        {/* Engagement tiles only when Postmark actually answered: rendering
            zeros for "unknown" would read as a campaign nobody opened. */}
        <S.StatMetrics>
          <Metric label="Sent" value={stats.sent} />
          {showEngagement && engagement && (
            <>
              <Metric label="Opened" value={engagement.opened} rate={share(engagement.opened)} />
              <Metric label="Clicked" value={engagement.clicked} rate={share(engagement.clicked)} />
              <Metric label="Bounced" value={engagement.bounced} />
              <Metric label="Spam" value={engagement.spamComplaints} />
            </>
          )}
        </S.StatMetrics>

        {stats.postmarkAvailable ? (
          <S.StatFootnote>
            Sent comes from Listmonk; opens, clicks, bounces and complaints come from Postmark, matched to
            this campaign by its tag. Rates are a share of Sent.
          </S.StatFootnote>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Engagement unavailable"
            description="Postmark's broadcast token isn't configured for this environment, so only the Listmonk send count is available."
          />
        )}
      </Flex>
    );
  };

  return (
    <Card
      title={
        <Flex align="center" gap={8}>
          Campaign stats
          {selected && <Tag>{selected.status}</Tag>}
        </Flex>
      }
      extra={selector}
    >
      {body()}
    </Card>
  );
};

export default CampaignStats;
